import { useContentDeliveryServiceV2 } from "@rw1/api-services";
import { ContentDelivery_V2 } from "@rw1/auto-generated-clients";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useImagePreloader } from "../../../hooks";
import {
  LIVE_RELEASE_CHANNEL_NAME,
  useAllExperiences,
} from "../../../hooks/useAllExperiences";
import type { TExperience } from "../types";
import { useIncomingLicenses } from "./useIncomingLicenses";

export type ExperienceDetail = TExperience & {
  description?: string | null;
  version?: string | null;
  imageUrl?: string | null;
  licenseStartDate?: Date | null;
  blockedSeats?: number | null;
  isExternalLimited?: boolean;
};

export const useExperienceById = (): {
  exp: ExperienceDetail | null;
  loading: boolean;
} => {
  const { experienceId } = useParams<{ experienceId: string }>();
  const { companyId } = useParams();
  const contentDeliveryV2 = useContentDeliveryServiceV2();

  // 1. Fetch Base Data
  const { experiences, isLoading: isExperiencesLoading } = useAllExperiences({
    releaseChannelName: LIVE_RELEASE_CHANNEL_NAME,
    enabled: true,
    pageSize: 1000,
    includeBinaries: false,
  });

  // 2. Fetch License Data
  const { licensesByReleaseChannel, isLoading: isLicensesLoading } =
    useIncomingLicenses();

  // 3. Find the specific RC
  const rc = useMemo(
    () => experiences.find((x) => x.releaseChannelId === experienceId),
    [experiences, experienceId],
  );

  const hasMetadata = !!rc?.contentMetadataId;

  // 4. Fetch Heavy Metadata ONLY for this specific item (Get By ID)
  const { data: specificMetadata, isLoading: isMetadataLoading } = useQuery<
    ContentDelivery_V2.ContentMetadata | undefined
  >({
    queryKey: ["experience-metadata-by-id", rc?.contentMetadataId],
    enabled: hasMetadata,
    queryFn: async () => {
      if (!rc?.contentMetadataId) return undefined;

      return await contentDeliveryV2.getContentMetadataById(
        rc.contentMetadataId,
      );
    },
    staleTime: 1000 * 60 * 5,
  });

  const exp = useMemo<ExperienceDetail | null>(() => {
    if (!experienceId || !rc) return null;

    const isInternal = Boolean(
      rc.companyId && companyId && rc.companyId === companyId,
    );

    const license = licensesByReleaseChannel.get(experienceId);

    let total = rc.seatsTotal ?? null;
    let used = rc.seatsUsed ?? 0;
    let expiresAt = rc.expirationDate ? new Date(rc.expirationDate) : null;

    if (license) {
      total = license.seats ?? null;
      used = license.allocatedSeats;
      expiresAt = license.expiresAt ? new Date(license.expiresAt) : null;
    }

    const blocked = rc.blockedSeats ?? null;
    const isExternalLimited = !isInternal && total != null;

    const finalVersion = rc.version ?? null;

    const finalDescription =
      specificMetadata?.description ?? rc.description ?? null;

    const finalImageUrl =
      specificMetadata?.libraryCardUrl ?? rc.cardImageUrl ?? null;

    return {
      id: rc.releaseChannelId,
      name: rc.friendlyName || rc.releaseChannelName,
      companyId: rc.companyId || undefined,
      companyName: rc.companyName ?? "—",
      isInternal,
      seats: { used, total },
      expiresAt,
      description: finalDescription,
      version: finalVersion,
      imageUrl: finalImageUrl,
      licenseStartDate: rc.licenseStartDate
        ? new Date(rc.licenseStartDate)
        : null,
      blockedSeats: blocked,
      isExternalLimited,
    };
  }, [rc, experienceId, licensesByReleaseChannel, companyId, specificMetadata]);

  const isImageReady = useImagePreloader(exp?.imageUrl);

  const isDataLoading =
    isExperiencesLoading ||
    isLicensesLoading ||
    (hasMetadata && isMetadataLoading);

  return {
    exp,
    loading: isDataLoading || !experienceId || !isImageReady,
  };
};
